import { AccessReportResourcesProvider } from "context/accessReportResourcesContext";
import { AccessReviewReportsProvider } from "context/accessReviewReportsContext";
import { AccessReviewResourcesProvider } from "context/accessReviewResourcesContext";
import { AccessReviewsProvider } from "context/accessReviewsContext";
import { AccessReviewSubordinatesProvider } from "context/accessReviewSubordinatesContext";
import { AccessReviewTemplatesProvider } from "context/accessReviewTemplatesContext";
import { AdminLiveUpdatesProvider } from "context/adminLiveUpdatesContext";
import { AdminsProvider } from "context/adminsContext";
import { AgentTokensProvider } from "context/agentTokensContext";
import { ApiTokensProvider } from "context/apiTokensContext";
import { ApplicationsProvider } from "context/applicationsContext";
import { ApprovalAlgorithmsProvider } from "context/approvalAlgorithmsContext";
import { ApprovalFlowsProvider } from "context/approvalFlowContext";
import { AuthenticatedUserProvider } from "context/AuthenticatedUserContext";
import { BreakpointsProvider } from "context/breakpointsContext";
import { BulkActionsUpdatesProvider } from "context/bulkActionsUpdatesContext";
import { CompanyProvider } from "context/companyContext";
import { LegacyDirectoryConfigurationsProvider } from "context/directoryConfigurationsContext";
import { DirectoryGroupsProvider } from "context/directoryGroupsContext";
import { EscKeyDownEventListenerProvider } from "context/escKeyDownEventListenersContext";
import { GlobalErrorProvider } from "context/globalErrorContext";
import { IdentityProvidersProvider } from "context/identityProvidersContext";
import { IntegrationsProvider } from "context/integrationsContext";
import { LiveUpdatesProvider } from "context/liveUpdatesContext";
import { ObserverProvider } from "context/observerContext";
import { OnCallIntegrationSchedulesProvider } from "context/onCallIntegrationSchedulesContext";
import { OverlaysProvider } from "context/overlaysContext";
import { PropgationProvider } from "context/propagationContext";
import { Provider } from "context/Provider";
import { RedirectModalProvider } from "context/redirectModalContext";
import { SideNavProvider } from "context/sideNavContext";
import { SlackChannelsProvider } from "context/slackChannelsContext";
import { TicketUpdatesProvider } from "context/ticketUpdatesContext";
import { UsersProvider } from "context/usersContext";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { AuthGate } from "./AuthGate";
import { DefaultErrorBoundary } from "./components/ErrorBoundary";
import { LaunchDarklyProvider } from "./LaunchDarklyProvider";
import { QueryClientWithErrorProvider } from "./QueryClientWithErrorProvider";
import { ThemeProvider } from "./ThemeProvider";

const UnhandledPromiseCatcher: FC = ({ children }) => {
  const openGlobalErrorModal = useOpenGlobalErrorModal();
  useEffect(() => {
    const callback = (event: PromiseRejectionEvent) => openGlobalErrorModal(event.reason);
    window.addEventListener("unhandledrejection", callback);

    return () => {
      window.removeEventListener("unhandledrejection", callback);
    };
  }, [openGlobalErrorModal]);

  return <>{children}</>;
};

export const Providers: FC = ({ children }) => {
  return (
    <BreakpointsProvider>
      <ErrorBoundary FallbackComponent={DefaultErrorBoundary}>
        <ThemeProvider>
          <GlobalErrorProvider>
            <UnhandledPromiseCatcher>
              <QueryClientWithErrorProvider>
                <LiveUpdatesProvider>
                  <AuthenticatedUserProvider>
                    <CompanyProvider>
                      <OverlaysProvider>
                        <AuthGate>
                          <LaunchDarklyProvider>
                            <Provider
                              // The order does matter! do not change
                              // There are multiple levels of providers, because some contexts depend on others
                              providers={[
                                IntegrationsProvider,
                                AdminLiveUpdatesProvider,
                                AccessReviewResourcesProvider,
                                AccessReviewSubordinatesProvider,
                                ApprovalAlgorithmsProvider,
                                ApprovalFlowsProvider,
                              ]}
                            >
                              <Provider
                                providers={[
                                  AccessReportResourcesProvider,
                                  AccessReviewReportsProvider,
                                  AccessReviewTemplatesProvider,
                                  AccessReviewsProvider,
                                  AdminsProvider,
                                  AgentTokensProvider,
                                  ApiTokensProvider,
                                  ApplicationsProvider,
                                  BulkActionsUpdatesProvider,
                                  LegacyDirectoryConfigurationsProvider,
                                  DirectoryGroupsProvider,
                                  IdentityProvidersProvider,
                                  OnCallIntegrationSchedulesProvider,
                                  PropgationProvider,
                                  RedirectModalProvider,
                                  SideNavProvider,
                                  TicketUpdatesProvider,
                                  UsersProvider,
                                  ObserverProvider,
                                  EscKeyDownEventListenerProvider,
                                  SlackChannelsProvider,
                                ]}
                              >
                                <>{children}</>
                              </Provider>
                            </Provider>
                          </LaunchDarklyProvider>
                        </AuthGate>
                      </OverlaysProvider>
                    </CompanyProvider>
                  </AuthenticatedUserProvider>
                </LiveUpdatesProvider>
              </QueryClientWithErrorProvider>
            </UnhandledPromiseCatcher>
          </GlobalErrorProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BreakpointsProvider>
  );
};
